import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BASE_API_URL } from '@cnt-workspace/data-access';
import {
  DateRange,
  IAnalyticsDeletions,
  IAnalyticsGuests,
  IAnalyticsHosts,
  IAnalyticsNudge,
  IAnalyticsOverview,
  IAnalyticsSales,
  IAnalyticsToday,
  IReservation,
  IReservationCounts,
  IReservationExtra,
  IReservationRequestWindow,
  IReservationWhere,
  IUser,
  IWeeklyReport,
  PaymentStatus,
} from '@curbnturf/entities';
import { Observable } from 'rxjs';

const API_URL = BASE_API_URL + 'admin/reservation';

/** The reservation list's day ranges (ISO days, both ends included); status changes are read in the browser's zone. */
export interface IAdminReservationDates {
  checkInFrom?: string;
  checkInTo?: string;
  changedFrom?: string;
  changedTo?: string;
}

/** One page of `GET /admin/reservation/paginated`; `total` counts every match. */
export interface IAdminReservationPage {
  total: number;
  page: number;
  reservations: IReservation[];
}

/** Server sort keys for the reservation list. */
export type AdminReservationSort = 'id' | 'date' | 'guest' | 'listing' | 'status';

export interface IAdminRequestWindow extends IReservationRequestWindow {
  adminExtendedBy?: IUser | null;
}

export interface IAdminModifyInput {
  date: { from: string; to: string };
  rvId?: number;
  extras?: IReservationExtra[];
  refundMethod?: 'card' | 'cash';
  note?: string;
}

export interface IAdminModifyQuote {
  originalTotal: number;
  newTotal: number;
  /** Positive: owed by the guest — CNT Cash first, then the card. Negative: refunded. */
  difference: number;
  hostNetDelta: number;
  /** Most that can go back to the card; any more is refunded as CNT Cash. */
  cardRefundable: number;
  /** On an increase, the guest's CNT Cash that goes toward it before the card (0 otherwise). */
  cashApplicable: number;
}

export interface IAdminCommunicationChannel {
  id: number;
  kind: 'reservation' | 'pre-reservation' | 'other-reservation';
  reservationId?: number | null;
  timestamp?: number;
  messages: {
    id: number;
    fromId?: number;
    toId?: number;
    fromName?: string;
    body: string;
    timestamp: number;
    read: boolean;
  }[];
}

export interface IAdminLedgerRow {
  id: number;
  timestamp: number;
  userId: number;
  event: string;
  value: number;
  note: string;
  privateNote: string;
  status: string;
}

export interface IAdminReservationTransactions {
  payments: {
    purchaseId: string;
    timestamp: number;
    amount: number;
    transactionId: string;
    originalTransactionId?: string;
    status: PaymentStatus;
  }[];
  cash: IAdminLedgerRow[];
  accountBalance: IAdminLedgerRow[];
}

/** What a cancel refunded the guest (as CNT Cash); `issued: false` means nothing, and `reason` says why. */
export interface IAdminCancelRefund {
  amount: number;
  issued: boolean;
  reason: string;
}

/** Whose cancellation an admin cancel is: the host backing out, the guest, or no one's fault (e.g. weather). */
export type AdminCancelParty = 'host' | 'guest' | 'noFault';

/** On a chosen refund: void the host's payout, or take the refund out of it (then their balance). */
export type AdminCancelHostPayout = 'void' | 'reduce';

/** Admin-only reservation actions and views (`/admin/reservation`, AdminAuthGuard). */
@Injectable({ providedIn: 'root' })
export class AdminReservationApiService {
  private readonly http = inject(HttpClient);

  /** Waiting reservations per status, and how many are overdue. */
  counts(): Observable<IReservationCounts> {
    return this.http.get<IReservationCounts>(`${API_URL}/counts`);
  }

  /** One page of reservations matching `where` and `dates`, sorted on the server. */
  search(
    where: IReservationWhere,
    page = 1,
    pageSize = 50,
    sort: AdminReservationSort = 'id',
    dir: 'asc' | 'desc' = 'desc',
    dates: IAdminReservationDates = {},
  ): Observable<IAdminReservationPage> {
    const statuses = where.status ? [where.status].flat() : [];
    const params: Record<string, string | number> = { page, pageSize, sort, dir };
    if (where.userIds?.length) params['userIds'] = where.userIds.join(',');
    if (where.ownerIds?.length) params['ownerIds'] = where.ownerIds.join(',');
    if (where.siteIds?.length) params['siteIds'] = where.siteIds.join(',');
    if (statuses.length) params['status'] = statuses.join(',');
    if (where.rated !== undefined) params['rated'] = String(where.rated);
    for (const [key, day] of Object.entries(dates)) if (day) params[key] = day;
    if (dates.changedFrom || dates.changedTo) Object.assign(params, zoneParams());
    return this.http.get<IAdminReservationPage>(`${API_URL}/paginated`, { params });
  }

  /** The analytics Overview for `range` against `compare`, bucketed in the browser's time zone. */
  analytics(range: DateRange, compare: DateRange): Observable<IAnalyticsOverview> {
    return this.http.get<IAnalyticsOverview>(`${API_URL}/analytics`, { params: analyticsParams(range, compare) });
  }

  /** The analytics Guests tab for `range` against `compare`, in the browser's time zone. */
  analyticsGuests(range: DateRange, compare: DateRange): Observable<IAnalyticsGuests> {
    return this.http.get<IAnalyticsGuests>(`${API_URL}/analytics/guests`, { params: analyticsParams(range, compare) });
  }

  /** The analytics Hosts tab for `range` against `compare`, in the browser's time zone. */
  analyticsHosts(range: DateRange, compare: DateRange): Observable<IAnalyticsHosts> {
    return this.http.get<IAnalyticsHosts>(`${API_URL}/analytics/hosts`, { params: analyticsParams(range, compare) });
  }

  /** The analytics Sales tab for `range` against `compare`, in the browser's time zone. */
  analyticsSales(range: DateRange, compare: DateRange): Observable<IAnalyticsSales> {
    return this.http.get<IAnalyticsSales>(`${API_URL}/analytics/sales`, { params: analyticsParams(range, compare) });
  }

  /** "Hosts to nudge", with "today" in the browser's time zone. */
  analyticsNudge(): Observable<IAnalyticsNudge> {
    return this.http.get<IAnalyticsNudge>(`${API_URL}/analytics/nudge`, { params: zoneParams() });
  }

  /** The weekly report for `range` (a week or a custom range), in the browser's time zone. */
  analyticsWeekly(range: DateRange): Observable<IWeeklyReport> {
    return this.http.get<IWeeklyReport>(`${API_URL}/analytics/weekly`, {
      params: { start: range.start.toISO() ?? '', end: range.end.toISO() ?? '', ...zoneParams() },
    });
  }

  /** Home's "Today at the stays", for today in the browser's time zone. */
  analyticsToday(): Observable<IAnalyticsToday> {
    return this.http.get<IAnalyticsToday>(`${API_URL}/analytics/today`, { params: zoneParams() });
  }

  /** Home's "Deleted accounts to check", with "today" in the browser's time zone. */
  analyticsDeletions(): Observable<IAnalyticsDeletions> {
    return this.http.get<IAnalyticsDeletions>(`${API_URL}/analytics/deletions`, { params: zoneParams() });
  }

  requestWindows(id: number): Observable<IAdminRequestWindow[]> {
    return this.http.get<IAdminRequestWindow[]>(`${API_URL}/${id}/request-windows`);
  }

  extend(id: number, hours: number, note?: string): Observable<IAdminRequestWindow> {
    return this.http.post<IAdminRequestWindow>(`${API_URL}/${id}/extend`, { hours, note });
  }

  accept(id: number, reason?: string): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${id}/accept`, { reason });
  }

  reject(id: number, reason?: string): Observable<IReservation> {
    return this.http.post<IReservation>(`${API_URL}/${id}/reject`, { reason });
  }

  /**
   * Omit `refundAmount` to apply the listing's cancellation policy. `onBehalfOf` is whose cancellation
   * it is, so it counts against them (e.g. a host backing out); `noFault` counts against neither.
   */
  cancel(
    id: number,
    reason: string | undefined,
    refundAmount: number | undefined,
    onBehalfOf: AdminCancelParty,
    hostPayout?: AdminCancelHostPayout,
  ): Observable<IReservation & { refund?: IAdminCancelRefund }> {
    return this.http.post<IReservation & { refund?: IAdminCancelRefund }>(`${API_URL}/${id}/cancel`, {
      reason,
      refundAmount,
      onBehalfOf,
      hostPayout,
    });
  }

  quoteModify(id: number, input: IAdminModifyInput): Observable<IAdminModifyQuote> {
    return this.http.post<IAdminModifyQuote>(`${API_URL}/${id}/modify/quote`, input);
  }

  modify(id: number, input: IAdminModifyInput): Observable<IReservation> {
    return this.http.put<IReservation>(`${API_URL}/${id}/modify`, input);
  }

  communications(id: number): Observable<IAdminCommunicationChannel[]> {
    return this.http.get<IAdminCommunicationChannel[]>(`${API_URL}/${id}/communications`);
  }

  transactions(id: number): Observable<IAdminReservationTransactions> {
    return this.http.get<IAdminReservationTransactions>(`${API_URL}/${id}/transactions`);
  }
}

/** The browser's time zone. */
function zoneParams(): Record<string, string> {
  return { tz: Intl.DateTimeFormat().resolvedOptions().timeZone };
}

/** A period and the one it's compared with, as ISO, plus the browser's time zone. */
function analyticsParams(range: DateRange, compare: DateRange): Record<string, string> {
  return {
    start: range.start.toISO() ?? '',
    end: range.end.toISO() ?? '',
    compareStart: compare.start.toISO() ?? '',
    compareEnd: compare.end.toISO() ?? '',
    ...zoneParams(),
  };
}
